import type {
  CoachingSession,
  ParticipationReport,
} from '@efdigital/ll-addon-data';
import type CoachService from '@efdigital/ll-xceed-v2/services/coach';
import {
  buildCoachAttendanceMarkBody,
  confirmedDurationOptions,
  scheduledDurationMinutes,
} from '@efdigital/ll-xceed-v2/utils/coaching/coach-attendance-mark-body';
import { participationReportLabel } from '@efdigital/ll-xceed-v2/utils/coaching/participation-report-display';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import BsModal from 'ember-bootstrap/components/bs-modal';

function durationSelected(selected: number | null, minutes: number): boolean {
  return selected === minutes;
}

interface ConfirmAttendanceModalSignature {
  Args: {
    session: CoachingSession;
    onChanged?: () => void | Promise<void>;
  };
  Blocks: {
    default: [showModal: () => void];
  };
}

export class ConfirmAttendanceModal extends Component<ConfirmAttendanceModalSignature> {
  @service declare coach: CoachService;
  @tracked open = false;
  @tracked loading = false;
  @tracked report: ParticipationReport = 'session_delivered';
  @tracked issueDetails = '';
  @tracked confirmedDurationMinutes: number | null = null;
  @tracked errorMessage = '';

  closeModal = () => {
    this.open = false;
  };

  get isCoachingSession(): boolean {
    return this.args.session.sessionType === 'coaching';
  }

  get scheduledMinutes(): number | null {
    try {
      return scheduledDurationMinutes(this.args.session);
    } catch {
      return null;
    }
  }

  get durationOptions(): number[] {
    return this.scheduledMinutes == null
      ? []
      : confirmedDurationOptions(this.scheduledMinutes);
  }

  get requiresConfirmedDuration(): boolean {
    return this.isSessionDelivered && this.isCoachingSession;
  }

  get confirmButtonDisabled(): boolean {
    if (this.loading) {
      return true;
    }

    if (this.isNeedsReview) {
      return this.issueDetails.trim().length === 0;
    }

    if (this.requiresConfirmedDuration && !this.confirmedDurationMinutes) {
      return true;
    }

    return false;
  }

  submitReport = async () => {
    if (this.confirmButtonDisabled) {
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    try {
      await this.coach.markAttendance(
        this.args.session,
        buildCoachAttendanceMarkBody({
          report: this.report,
          sessionType: this.args.session.sessionType,
          issueDetails: this.issueDetails,
          confirmedDurationMinutes: this.confirmedDurationMinutes,
        })
      );
      await this.args.onChanged?.();
      this.open = false;
    } catch {
      this.errorMessage = 'Could not submit your report. Please try again.';
    } finally {
      this.loading = false;
    }
  };

  showModal = () => {
    this.report = 'session_delivered';
    this.issueDetails = '';
    this.errorMessage = '';
    this.confirmedDurationMinutes = this.isCoachingSession
      ? this.scheduledMinutes
      : null;
    this.open = true;
  };

  setSessionDelivered = () => {
    this.report = 'session_delivered';
  };

  setIDidNotAttend = () => {
    this.report = 'i_did_not_attend';
  };

  setOtherDidNotAttend = () => {
    this.report = 'other_did_not_attend';
  };

  setNeedsReview = () => {
    this.report = 'reported_issue';
  };

  updateIssueDetails = (event: Event) => {
    this.issueDetails = (event.target as HTMLTextAreaElement).value;
  };

  setConfirmedDuration = (event: Event) => {
    const value = parseInt((event.target as HTMLSelectElement).value, 10);
    this.confirmedDurationMinutes = Number.isFinite(value) ? value : null;
  };

  get isSessionDelivered(): boolean {
    return this.report === 'session_delivered';
  }

  get isIDidNotAttend(): boolean {
    return this.report === 'i_did_not_attend';
  }

  get isOtherDidNotAttend(): boolean {
    return this.report === 'other_did_not_attend';
  }

  get isNeedsReview(): boolean {
    return this.report === 'reported_issue';
  }

  <template>
    {{#if this.open}}
      <BsModal @open={{this.open}} as |modal|>
        <modal.header>
          <h5 class="modal-title">Report session outcome</h5>
        </modal.header>
        <modal.body aria-busy={{this.loading}}>
          <p>What happened in this session?</p>
          <div class="form-check">
            <input
              class="form-check-input"
              type="radio"
              name="coach-participation-report-{{@session.id}}"
              id="coach-report-delivered-{{@session.id}}"
              checked={{this.isSessionDelivered}}
              {{on "change" this.setSessionDelivered}}
            />
            <label
              class="form-check-label"
              for="coach-report-delivered-{{@session.id}}"
            >
              {{participationReportLabel "session_delivered" "coach"}}
            </label>
          </div>
          <div class="form-check">
            <input
              class="form-check-input"
              type="radio"
              name="coach-participation-report-{{@session.id}}"
              id="coach-report-self-absent-{{@session.id}}"
              checked={{this.isIDidNotAttend}}
              {{on "change" this.setIDidNotAttend}}
            />
            <label
              class="form-check-label"
              for="coach-report-self-absent-{{@session.id}}"
            >
              {{participationReportLabel "i_did_not_attend" "coach"}}
            </label>
          </div>
          <div class="form-check">
            <input
              class="form-check-input"
              type="radio"
              name="coach-participation-report-{{@session.id}}"
              id="coach-report-other-absent-{{@session.id}}"
              checked={{this.isOtherDidNotAttend}}
              {{on "change" this.setOtherDidNotAttend}}
            />
            <label
              class="form-check-label"
              for="coach-report-other-absent-{{@session.id}}"
            >
              {{participationReportLabel "other_did_not_attend" "coach"}}
            </label>
          </div>
          <div class="form-check mb-3">
            <input
              class="form-check-input"
              type="radio"
              name="coach-participation-report-{{@session.id}}"
              id="coach-report-needs-review-{{@session.id}}"
              checked={{this.isNeedsReview}}
              {{on "change" this.setNeedsReview}}
            />
            <label
              class="form-check-label"
              for="coach-report-needs-review-{{@session.id}}"
            >
              {{participationReportLabel "reported_issue" "coach"}}
            </label>
          </div>
          {{#if this.requiresConfirmedDuration}}
            <div class="mb-3">
              <label
                class="form-label"
                for="coach-confirmed-duration-{{@session.id}}"
              >
                Confirmed duration
              </label>
              <select
                class="form-select"
                id="coach-confirmed-duration-{{@session.id}}"
                disabled={{this.loading}}
                data-test-coach-confirmed-duration
                {{on "change" this.setConfirmedDuration}}
              >
                {{#each this.durationOptions as |mins|}}
                  <option
                    value={{mins}}
                    selected={{durationSelected
                      this.confirmedDurationMinutes
                      mins
                    }}
                  >{{mins}} min</option>
                {{/each}}
              </select>
              <div class="form-text">Scheduled:
                {{if this.scheduledMinutes this.scheduledMinutes "unknown"}}
                {{if this.scheduledMinutes "min"}}</div>
            </div>
          {{/if}}
          {{#if this.isNeedsReview}}
            <div class="mb-3">
              <label
                class="form-label"
                for="coach-issue-details-{{@session.id}}"
              >
                What happened?
              </label>
              <textarea
                id="coach-issue-details-{{@session.id}}"
                class="form-control"
                rows="4"
                value={{this.issueDetails}}
                placeholder="Describe what happened and any follow-up needed."
                {{on "input" this.updateIssueDetails}}
              ></textarea>
            </div>
            <p class="text-body-secondary small mb-0">
              We will review your report and take appropriate action. You can
              also email
              <a
                href="mailto:v-support@hultashridge.com"
              >v-support@hultashridge.com</a>
              if you need immediate help.
            </p>
          {{/if}}
          {{#if this.errorMessage}}
            <p
              class="text-danger mb-0 mt-3"
              role="alert"
            >{{this.errorMessage}}</p>
          {{/if}}
        </modal.body>
        <modal.footer>
          <button
            class="btn btn-outline-primary"
            type="button"
            {{on "click" this.closeModal}}
            disabled={{this.loading}}
          >Cancel</button>
          <button
            class="btn btn-primary"
            type="button"
            {{on "click" this.submitReport}}
            disabled={{this.confirmButtonDisabled}}
            aria-busy={{this.loading}}
          >Submit report</button>
        </modal.footer>
      </BsModal>
    {{/if}}
    {{yield this.showModal}}
  </template>
}
